import "./AppLayout.scss"

import { useState, useEffect, useLayoutEffect, useContext } from 'react';
import PropTypes            from 'prop-types'
import classnames           from 'classnames'
import { MasterContext }    from 'master_context'

import SideNav              from 'SideNav'
import Navigation           from 'Navigation'

import WhiteSanaLogo from 'lib/images/primary-logo-white.svg';
import MoreIcon from 'icons/global/outline_24px/header_more_24px'
import FlashMessageQueue    from 'FlashMessageQueue'

export default function AppLayout({ children }) {
  const [adminCompanies] = useState(INITIAL_DATA.adminCompanies);
  const [isBroker] = useState(INITIAL_DATA.isBroker);
  const [bundleName] = useState(INITIAL_DATA.bundleName);
  const [currentUser] = useState(INITIAL_DATA.currentUser);
  const [currentCompany] = useState(INITIAL_DATA.currentCompany);
  const [loggedInAs] = useState(INITIAL_DATA.loggedInAs);
  const [navigationVisible, setNavigationVisible] = useState(false);
  const [offerings] = useState(INITIAL_DATA.navOfferings);
  const [internalEternalVisibleNav] = useState(INITIAL_DATA.internalEternalVisibleNav);
  const [permissions] = useState(INITIAL_DATA.permissions);

  const { PopoverContext, ShowNavContext, WindowSizeContext } = useContext(MasterContext);
  const { popover } = PopoverContext;
  const { handleToggleMoreNav } = ShowNavContext;
  const { updateWidth } = WindowSizeContext;

  const {
    firstNameOrEmail,
    nickname,
    firstName,
    lastName,
    personType,
  } = currentUser;

  const friendlyName = nickname || firstNameOrEmail.split('@')[0];
  const shortenedName = friendlyName.length > 12 ? `${friendlyName.slice(0, 12)}...` : friendlyName;

  const enableNewNavigation = bundleName !== "InternalApp";

  const isMember = personType !== null;

  useEffect(() => {
    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  const handleToggleNavigationVisible = (e) => {
    if (bundleName === "InternalApp") {
      handleToggleMoreNav(e);
    } else {
      setNavigationVisible(!navigationVisible);
    }
  }

  const hasAdminMember = () => {
    return adminCompanies.length > 0
  }

  return (
    <>
      <div
        className={classnames(
          "AppLayout",
          'AppLayout--default-content-sizing',
          {"updated-navigation": enableNewNavigation},
          {"internal-eternal-visible-nav": internalEternalVisibleNav}
        )}
      >
        {enableNewNavigation ?
          <Navigation
            bundleName={bundleName}
            visible={navigationVisible}
            handleToggleNavigationVisible={handleToggleNavigationVisible}
            offerings={offerings}
            currentCompany={currentCompany}
            adminCompanies={adminCompanies}
            permissions={permissions}
            profileName={nickname || shortenedName}
            firstName={firstName}
            lastName={lastName}
            isAdmin={hasAdminMember()}
            isBroker={isBroker}
            isMember={isMember}
          />
          :
          <SideNav
            bundleName={bundleName}
            hasAdminMember={hasAdminMember}
            isMember={isMember}
            permissions={permissions}
            shortenedName={shortenedName}
          />
        }
        <div className="page-content">
          { loggedInAs && (<div className="logged-in-as-message">
            You are currently logged in as {currentUser.fullNameOrEmail} (User {currentUser.id}). Log out to return to internal tools.
          </div>) }

          <header className="global-top">
            <div className="top-nav">
              <div className="logo-wrapper">
                <a href="/">
                  <WhiteSanaLogo />
                </a>
              </div>
              <div className="more-button-container">
                <button className="more-button" onClick={handleToggleNavigationVisible}>
                  <MoreIcon className="icon" />
                </button>
              </div>
            </div>
          </header>
          <main className="content-wrapper">
            <FlashMessageQueue />
            {children}
          </main>
        </div>
      </div>
      { popover }
    </>
  );
}

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
  mobileStore: PropTypes.shape({
    updateWidth: PropTypes.func,
  }),
}

// Okay, if anybody else has set this, removing it will break. Hmm.
// Raise an error if more than one person sets it?
// TODO: do all unmount effects run before mount effects?
export function useLockedHeightAppLayout() {
  useLayoutEffect(() => {
    document.querySelector('.page-content').classList.add("page-content--locked-height");

    return () => {
      document.querySelector('.page-content').classList.remove("page-content--locked-height");
    };
  }, [])
}

export function useCustomContentPadding() {
  useLayoutEffect(() => {
    document.querySelector('.AppLayout').classList.remove("AppLayout--default-content-sizing");

    return () => {
      document.querySelector('.AppLayout').classList.add("AppLayout--default-content-sizing");
    };
  }, [])
}
